// frontend/src/components/inventory/PartnerSelector.js
import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPlus, FaTimes, FaChevronDown, FaUser, FaBuilding } from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../ui/Loader';

const PartnerSelector = ({ 
  value, 
  onChange, 
  type, // 'customer' or 'supplier'
  required = false,
  placeholder,
  allowCreate = true,
  maxDisplayItems = 10
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [createLoading, setCreateLoading] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });
  
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  
  const typeConfig = {
    customer: { label: '客户', icon: FaUser, placeholder: '选择或搜索客户' },
    supplier: { label: '供应商', icon: FaBuilding, placeholder: '选择或搜索供应商' }
  };
  
  const config = typeConfig[type] || typeConfig.customer;
  const finalPlaceholder = placeholder || config.placeholder;
  
  // 从value中找到选中的合作伙伴
  useEffect(() => {
    if (value && partners.length > 0) {
      const partner = partners.find(p => p.name === value || p.id.toString() === value.toString());
      setSelectedPartner(partner);
    } else if (!value) {
      setSelectedPartner(null);
    }
  }, [value, partners]);
  
  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // 搜索合作伙伴
  const searchPartners = async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type });
      if (search) params.append('search', search);
      
      const response = await api.get(`/partners?${params}`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error searching partners:', error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 初始加载合作伙伴
  useEffect(() => {
    searchPartners();
  }, [type]);
  
  // 搜索词变化时搜索
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchPartners(searchTerm);
      setHighlightedIndex(-1);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, type]);
  
  // 键盘导航
  const handleKeyDown = (e) => {
    const filteredPartners = partners.filter(partner =>
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (partner.contact_person && partner.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredPartners.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredPartners.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredPartners[highlightedIndex]) {
          handlePartnerSelect(filteredPartners[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setShowCreateForm(false);
        break;
    }
  };
  
  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner);
    onChange(partner.name); // 返回名称，保持与现有API兼容
    setIsOpen(false);
    setSearchTerm('');
    setShowCreateForm(false);
  };
  
  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 100);
  };
  
  const handleShowCreateForm = () => {
    setNewPartnerData(prev => ({ ...prev, name: searchTerm }));
    setShowCreateForm(true);
  };
  
  const handleCreateNew = async () => {
    if (!newPartnerData.name.trim()) {
      alert('请输入名称');
      return;
    }
    
    setCreateLoading(true);
    try {
      const partnerData = {
        ...newPartnerData,
        type,
        name: newPartnerData.name.trim()
      };
      
      const response = await api.post('/partners', partnerData);
      const newPartner = response.data;
      
      // 刷新合作伙伴列表
      await searchPartners(searchTerm);
      
      // 自动选择新创建的合作伙伴
      handlePartnerSelect(newPartner);
      
      // 重置表单
      setNewPartnerData({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        notes: ''
      });
      
    } catch (error) {
      console.error('创建合作伙伴失败:', error);
      alert(error.response?.data?.error || '创建失败');
    } finally {
      setCreateLoading(false);
    }
  };
  
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedPartner(null);
    onChange('');
  };
  
  const filteredPartners = partners.filter(partner =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (partner.contact_person && partner.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
  ).slice(0, maxDisplayItems);
  
  const IconComponent = config.icon;
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* 选择框 */}
      <div
        onClick={handleOpen}
        className={`w-full p-2 border rounded-lg cursor-pointer bg-white flex items-center justify-between ${
          isOpen ? 'border-blue-500' : 'border-gray-300'
        } ${required && !value ? 'border-red-300' : ''}`}
      >
        <div className="flex items-center flex-1">
          <IconComponent className="text-gray-400 mr-2" />
          {selectedPartner ? (
            <div>
              <div className="font-medium">{selectedPartner.name}</div>
              {selectedPartner.contact_person && (
                <div className="text-sm text-gray-500">联系人: {selectedPartner.contact_person}</div>
              )}
            </div>
          ) : (
            <span className="text-gray-500">{finalPlaceholder}</span>
          )}
        </div>
        <div className="flex items-center">
          {selectedPartner && (
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 mr-2"
              title="清除选择"
            >
              <FaTimes />
            </button>
          )}
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* 下拉框 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* 搜索框 */}
          <div className="p-3 border-b">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`搜索${config.label}名称或联系人...`}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>
            {searchTerm && filteredPartners.length === 0 && !loading && allowCreate && (
              <div className="mt-2">
                <button
                  onClick={handleShowCreateForm}
                  className="w-full text-left text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center py-2 border border-blue-300 rounded hover:bg-blue-50"
                >
                  <FaPlus className="mr-2" />
                  创建新{config.label} "{searchTerm}"
                </button>
              </div>
            )}
          </div>
          
          {/* 创建新合作伙伴表单 */}
          {showCreateForm && allowCreate ? (
            <div className="p-4 bg-gray-50 border-b max-h-80 overflow-y-auto">
              <h4 className="font-medium mb-3 flex items-center">
                <FaPlus className="mr-2 text-green-600" />
                创建新{config.label}
              </h4>
              <div className="space-y-3">
                <div>
                  <input
                    type="text"
                    value={newPartnerData.name}
                    onChange={(e) => setNewPartnerData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={`${config.label}名称 *`}
                    className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newPartnerData.contact_person}
                    onChange={(e) => setNewPartnerData(prev => ({ ...prev, contact_person: e.target.value }))}
                    placeholder="联系人"
                    className="p-2 border rounded focus:outline-none focus:border-blue-500"
                  />
                  <input
                    type="tel"
                    value={newPartnerData.phone}
                    onChange={(e) => setNewPartnerData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="联系电话"
                    className="p-2 border rounded focus:outline-none focus:border-blue-500"
                  />
                </div>
                <input
                  type="email"
                  value={newPartnerData.email}
                  onChange={(e) => setNewPartnerData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="邮箱"
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
                <textarea
                  value={newPartnerData.address}
                  onChange={(e) => setNewPartnerData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="地址"
                  rows={2}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateNew}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex-1 flex items-center justify-center"
                    disabled={!newPartnerData.name.trim() || createLoading}
                  >
                    {createLoading ? (
                      <>
                        <Loader size="sm" />
                        <span className="ml-2">创建中...</span>
                      </>
                    ) : (
                      <>
                        <FaPlus className="mr-2" />
                        创建{config.label}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* 合作伙伴列表 */
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <Loader size="sm" />
                  <span className="ml-2 text-sm text-gray-500">搜索中...</span>
                </div>
              ) : filteredPartners.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="mb-2">没有找到匹配的{config.label}</div>
                  {allowCreate && searchTerm && (
                    <button
                      onClick={handleShowCreateForm}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center mx-auto"
                    >
                      <FaPlus className="mr-1" />
                      创建新{config.label} "{searchTerm}"
                    </button>
                  )}
                </div>
              ) : (
                filteredPartners.map((partner, index) => (
                  <div
                    key={partner.id}
                    onClick={() => handlePartnerSelect(partner)}
                    className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      selectedPartner?.id === partner.id ? 'bg-blue-50' : 
                      highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{partner.name}</div>
                    {partner.contact_person && (
                      <div className="text-sm text-gray-600">联系人: {partner.contact_person}</div>
                    )}
                    {partner.phone && (
                      <div className="text-sm text-gray-500">电话: {partner.phone}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* 底部添加合作伙伴链接 */}
          {!showCreateForm && allowCreate && filteredPartners.length > 0 && (
            <div className="border-t p-3 bg-gray-50">
              <button
                onClick={handleShowCreateForm}
                className="w-full text-left text-blue-600 hover:text-blue-800 text-sm flex items-center"
              >
                <FaPlus className="mr-2" />
                创建新{config.label}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerSelector;